import { loadItem, loadItems, ListAllTypes } from '../apis/dumpert';
import {
  fetchItemAction,
  fetchListAction,
  fetchMoreAction, selectItemsListIsLoaded,
  selectItemsListPage,
  selectNoMoreItems,
  toPositiveInt,
} from './items';
import {
  selectShowMedia,
  selectShowNsfw,
  selectFloppersDefaultSort,
  selectSearchDefaultSort,
} from './settings';
import { pathToCookie } from '../cookie';
import { selectDidomiConsentCookieAccepted, userDidomiConsentAcceptedAction } from './user';
import { adsSetKachingInitializedAction } from './ads';


/**
 * Calls API to load list of items and store result in global state
 * @param type -  one of the types latest, toppers, related or tag to search
 * @param listParams - fetch criteria
 * @returns {Function} operation to dispatch
 */
export const fetchItemsListOp = (type, listParams) => async (dispatch, getState, { monitor }) => {
  const state = getState();
  if (!ListAllTypes.includes(type)) {
    throw new Error(`Not supported list type ${type}`);
  }

  const filter = selectShowMedia(state);
  const nsfw = selectShowNsfw(state);
  const sorting = type === 'floppers'
    ? selectFloppersDefaultSort(state)
    : selectSearchDefaultSort(state);

  const createParams = type => {
    switch (type) {
    case 'tag':
    case 'zoek':
    case 'floppers':
      return ({ ...listParams, sorting: sorting });
    default:
      return listParams;
    }
  };

  const params = createParams(type);
  const payload = { type, filter, nsfw, params, page: toPositiveInt(params.pageNum) };

  dispatch(fetchListAction({ status: 'loading', ...payload }));
  let endTimer;
  const labels = { endpoint: `loadItems_${type}`};
  const { prometheus = { enabled: false } } = monitor || {};
  if(prometheus.enabled) {
    endTimer = prometheus.metrics.apiRequestDuration.startTimer();
  }
  try {
    const { data } = await loadItems(payload);
    if(prometheus.enabled) {
      endTimer({ ...labels, result: 'ok' });
    }
    dispatch(fetchListAction({
      ...payload,
      list: data.items,
      status: 'loaded',
      pageStops: [data.items.length],
      loadedAt: Date.now(),
    }));
  } catch (error) {
    if(prometheus.enabled) {
      endTimer({ ...labels, result: 'error' });
    }
    dispatch(fetchListAction({ error: error.toString(), ...payload }));
  }
};

/**
 * Calls API to fetch more items of already fetched list when scroll down
 * @param type
 * @param params
 * @returns {Function}
 */
export const fetchMoreOp = (type, params) => async (dispatch, getState) => {
  const state = getState();
  if (!selectItemsListIsLoaded(state, type) || selectNoMoreItems(state, type)) {
    return;
  }

  const page = type === 'top_dag' ||
               type === 'top_week' ||
               type === 'top_maand' ? params.pageNum : selectItemsListPage(state, type) + 1;
  const filter = pathToCookie(params.mediaFilter) || state.settings.showMedia;
  const nsfw = state.settings.showNsfw;
  const payload = { type, filter, nsfw, params, page };
  dispatch(fetchMoreAction({...payload, status: 'loadingMore' }));
  payload.status = 'loaded';
  try {
    const { data } = await loadItems(payload);
    if (data.items && data.items.length) {
      payload.items = data.items;
    } else {
      payload.noMoreItems = true;
    }
    dispatch(fetchMoreAction(payload));

  } catch (error) {
    dispatch(fetchMoreAction({ ...payload, error: error.toString()  }));
  }
};

/**
 * Calls API to load details of media item
 * @param itemId
 * @returns {Function}
 */
export const fetchItemOp = itemId => async (dispatch, getState, { monitor }) => {
  dispatch(fetchItemAction({ itemId, status: 'loading' }));
  let endTimer;
  const labels = { endpoint: 'loadItem' };
  const { prometheus = { enabled: false } } = monitor || {};
  if(prometheus.enabled) {
    endTimer = prometheus.metrics.apiRequestDuration.startTimer();
  }
  try {
    const { data } = await loadItem(itemId);
    if(prometheus.enabled) {
      endTimer({ ...labels, result: 'ok' });
    }
    dispatch(fetchItemAction({
      item: data.items[0],
      status: 'loaded',
    }));
  } catch (error) {
    if(prometheus.enabled) {
      endTimer({ ...labels, result: 'error' });
    }
    dispatch(fetchItemAction({ error: error.toString(), status: 'error' }));
  }
};

/**
 * Calls API to fetch from multiple endpoints in parallel on server
 * @param items - itemIds to fetch items
 * @param lists - criteria to fetch items lists
 * @returns {Function}
 */
export const batchFetchOp = ({ items, lists }) => async dispatch => {
  let fetches = [];
  if (items !== undefined ) {
    fetches = fetches.concat(items.map(itemId => dispatch(fetchItemOp(itemId))));
  }
  if (lists !== undefined) {
    fetches = fetches.concat(
      lists.map(
        ({ type, params }) => dispatch(fetchItemsListOp(type, params)),
      ),
    );
  }

  await Promise.all(fetches);
};

/**
 * Dispatch multiple actions based on user consent accepted values
 * @param didomiStatus
 * @returns {function(*, *): void}
 */
export const userDidomiConsentAcceptedOp = didomiStatus => (dispatch, getState) => {
  dispatch(userDidomiConsentAcceptedAction(didomiStatus));

  if(!selectDidomiConsentCookieAccepted(getState())) {
    dispatch(adsSetKachingInitializedAction(true));
  }
};
